import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { t, lang } = useLang();
  const { cart, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  if (cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-[#F0F0F0] flex items-center justify-center">
          <ShoppingBag className="w-10 h-10 text-[#E05D3A]" />
        </div>
        <h1 className="font-display mt-6 text-3xl font-black text-[#1A1A1A]">{t("emptyCart")}</h1>
        <p className="text-[#6B6B6B] mt-3">{t("startOrdering")}</p>
        <Button
          onClick={() => navigate("/restaurants")}
          className="mt-8 rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white px-8 py-6"
          data-testid="empty-cart-browse-btn"
        >
          {t("browseRestaurants")}
        </Button>
      </div>
    );
  }

  const deliveryFee = cart.restaurant?.delivery_fee || 10;
  const total = subtotal + deliveryFee;
  const restName = lang === "ar" ? cart.restaurant?.name_ar : cart.restaurant?.name_en;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 overflow-x-hidden">
      <h1 className="font-display text-3xl sm:text-4xl font-black text-[#1A1A1A]">{t("yourBasket")}</h1>
      <p className="text-[#6B6B6B] mt-2">{restName}</p>

      <div className="mt-8 grid md:grid-cols-[1fr_360px] gap-10">
        <div className="space-y-3 min-w-0">
          {cart.items.map((item) => (
            <div key={item.id} className="jh-card p-3 sm:p-4 flex items-center gap-3" data-testid={`cart-item-${item.id}`}>
              <img src={item.image} alt="" className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover flex-shrink-0 bg-[#F0F0F0]" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-[#1A1A1A] truncate">{lang === "ar" ? item.name_ar : item.name_en}</h3>
                <p className="text-[#E05D3A] font-bold mt-1">{item.price.toFixed(2)} SAR</p>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <button
                  onClick={() => updateQty(item.id, item.quantity - 1)}
                  className="w-8 h-8 rounded-full border border-[#EAEAEA] flex items-center justify-center hover:border-[#E05D3A]"
                  data-testid={`decrease-${item.id}`}
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-5 text-center font-semibold" data-testid={`qty-${item.id}`}>{item.quantity}</span>
                <button
                  onClick={() => updateQty(item.id, item.quantity + 1)}
                  className="w-8 h-8 rounded-full border border-[#EAEAEA] flex items-center justify-center hover:border-[#E05D3A]"
                  data-testid={`increase-${item.id}`}
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeItem(item.id)}
                  className="w-8 h-8 rounded-full text-[#6B6B6B] hover:text-red-600 flex items-center justify-center"
                  data-testid={`remove-${item.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="jh-card p-6 h-fit md:sticky md:top-24">
          <h3 className="font-bold text-lg text-[#1A1A1A]">{t("orderSummary")}</h3>
          <div className="mt-4 space-y-2 text-sm text-[#1A1A1A]">
            <div className="flex justify-between"><span>{t("subtotal")}</span><span>{subtotal.toFixed(2)} SAR</span></div>
            <div className="flex justify-between"><span>{t("deliveryFee")}</span><span>{deliveryFee.toFixed(2)} SAR</span></div>
            <div className="h-px bg-[#EAEAEA] my-3" />
            <div className="flex justify-between text-lg font-bold"><span>{t("total")}</span><span>{total.toFixed(2)} SAR</span></div>
          </div>
          <Button
            onClick={() => navigate("/checkout")}
            className="w-full mt-6 rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white py-6 text-base"
            data-testid="cart-checkout-btn"
          >
            {t("checkout")}
          </Button>
          <Link to="/restaurants" className="block text-center mt-4 text-sm text-[#6B6B6B] hover:text-[#E05D3A]">
            {t("continueShopping")}
          </Link>
        </div>
      </div>
    </div>
  );
}
